/**
 * Managed Database (OpenNebula, scope Internal.Opennebula.Database) on the v2
 * gateway service code **s10017**. Focuses on the MDS family (MySQL/PostgreSQL/
 * MariaDB); RDS shares the same shapes under /rds.
 *
 * Endpoints: /mds, /mds/:id (+ /monitor, /slaves, /credential, /actions, /resize),
 * /db/images, /db/flavors.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApi } from '@/api/client';
import { unwrap } from '@/api/opennebula';

export const DATABASE_SCOPE = 'Internal.Opennebula.Database';
const S10017 = 's10017';
const dbPath = (s: string, p: string) => `/v2/services/${s}/${S10017}/${p.replace(/^\/+/, '')}`;

const arr = <T,>(body: unknown, key: string): T[] =>
  Array.isArray(body) ? (body as T[]) : ((body as Record<string, unknown>)?.[key] as T[]) ?? [];

export const dbKeys = {
  list: (s: string) => ['db', s, 'mds'] as const,
  detail: (s: string, id: string) => ['db', s, 'mds', id] as const,
  monitor: (s: string, id: string) => ['db', s, 'mds', id, 'monitor'] as const,
  slaves: (s: string, id: string) => ['db', s, 'mds', id, 'slaves'] as const,
  flavors: (s: string) => ['db', s, 'flavors'] as const,
  images: (s: string) => ['db', s, 'images'] as const,
};

export interface Database {
  id: string;
  name?: string;
  status?: string;
  state?: string;
  engine?: string;
  type?: string;
  version?: string;
  endpoint?: string;
  ip?: string;
}

export interface DbFlavor {
  name: string;
  cores?: number;
  memory?: number;
}

export function useDatabases(serviceId: string | undefined) {
  return useQuery({
    queryKey: dbKeys.list(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => arr<Database>(unwrap(await serviceApi.get(dbPath(serviceId!, 'mds'))), 'mds'),
  });
}

export function useDatabase(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: dbKeys.detail(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    refetchInterval: 20_000,
    queryFn: async () => {
      const body = unwrap<{ mds?: Database } | Database>(await serviceApi.get(dbPath(serviceId!, `mds/${id}`)));
      return (body && typeof body === 'object' && 'mds' in body ? body.mds : body) as Database;
    },
  });
}

export function useDatabaseSlaves(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: dbKeys.slaves(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => arr<Database>(unwrap(await serviceApi.get(dbPath(serviceId!, `mds/${id}/slaves`))), 'slaves'),
  });
}

export function useCreateDatabase(serviceId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(dbPath(serviceId, 'mds'), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: dbKeys.list(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useDeleteDatabase(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(dbPath(serviceId, `mds/${id}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: dbKeys.list(serviceId) }); options?.onSuccess?.(...a); },
  });
}

